/**
 * G12: Alignment Prompt
 * Glass neumorphic inline modal for choosing boundary orientation
 * after import. Offers "Align horizontally" vs "Keep north at top".
 */

import './AlignmentPrompt.css'
import { t } from '@/i18n'

export type AlignmentChoice = 'align' | 'keep-north'

/**
 * Show the alignment prompt and return the user's choice.
 * Resolves when the user picks an option.
 */
export function showAlignmentPrompt(): Promise<AlignmentChoice> {
  return new Promise((resolve) => {
    // Create backdrop
    const backdrop = document.createElement('div')
    backdrop.className = 'alignment-prompt-backdrop'

    // Create modal
    const modal = document.createElement('div')
    modal.className = 'alignment-prompt'

    // Title
    const title = document.createElement('div')
    title.className = 'alignment-prompt-title'
    title.textContent = t('boundary.alignPrompt.title')
    modal.appendChild(title)

    // Options container
    const options = document.createElement('div')
    options.className = 'alignment-prompt-options'

    // Option 1: Align
    const alignBtn = createOption(
      'primary',
      'fa-solid fa-arrows-left-right',
      t('boundary.alignPrompt.align'),
      t('boundary.alignPrompt.alignDesc')
    )
    alignBtn.addEventListener('click', () => {
      cleanup()
      resolve('align')
    })
    options.appendChild(alignBtn)

    // Option 2: Keep North
    const keepBtn = createOption(
      'secondary',
      'fa-solid fa-compass',
      t('boundary.alignPrompt.keepNorth'),
      t('boundary.alignPrompt.keepNorthDesc')
    )
    keepBtn.addEventListener('click', () => {
      cleanup()
      resolve('keep-north')
    })
    options.appendChild(keepBtn)

    modal.appendChild(options)

    // Append
    document.body.appendChild(backdrop)
    document.body.appendChild(modal)

    // Close on backdrop click (treat as "keep north")
    backdrop.addEventListener('click', () => {
      cleanup()
      resolve('keep-north')
    })

    // Close on Escape
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanup()
        resolve('keep-north')
      }
    }
    document.addEventListener('keydown', onKeydown)

    function cleanup() {
      document.removeEventListener('keydown', onKeydown)
      backdrop.remove()
      modal.remove()
    }
  })
}

function createOption(
  variant: 'primary' | 'secondary',
  iconClass: string,
  label: string,
  description: string
): HTMLElement {
  const btn = document.createElement('button')
  btn.className = `alignment-prompt-option ${variant}`

  const icon = document.createElement('i')
  icon.className = iconClass
  btn.appendChild(icon)

  const textContainer = document.createElement('div')
  textContainer.className = 'alignment-prompt-option-text'

  const labelEl = document.createElement('div')
  labelEl.className = 'alignment-prompt-option-label'
  labelEl.textContent = label

  const descEl = document.createElement('div')
  descEl.className = 'alignment-prompt-option-desc'
  descEl.textContent = description

  textContainer.appendChild(labelEl)
  textContainer.appendChild(descEl)
  btn.appendChild(textContainer)

  return btn
}
